import { Divider, makeStyles } from '@material-ui/core';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { enumToStatusType, probabilities } from '../../../types/common';
import { Badge, CheckCard, Select } from '../../global';

const useDetailsClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    inputs: {
        display: 'flex',
        alignItems: 'center',
        margin: '10px -5px',
        '& > div': {
            flex: '1 0 calc(50% - 10px)',
            margin: '0 5px',
        },
    },
    badge: {
        display: 'inline-flex',
        margin: '-2px 0',
    },
    checkboxes: {
        display: 'flex',
        flexWrap: 'wrap',
        padding: 1,
        margin: '0 -5px',
        '& > *': {
            padding: 10,
            borderRadius: 5,
            margin: '0 5px',
            boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        },
    },
    activity: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: 40,
        '& > span': {
            color: theme.colors.darkBlue[5],
            ...theme.typography.headings.h3,
            marginBottom: 0,
        },
        '& > hr': {
            margin: '10px 0',
        },
    },
    methods: {
        display: 'flex',
        flexWrap: 'wrap',
        padding: 1,
        margin: -10,
        '& > div': {
            flex: '1 0 calc(100% / 3 - 50px)',
            minWidth: 200,
            margin: 10,
        },
    },
});

/**
 * Edit details of the inspection project, such as priority, and which methods will be executed.
 */
export const Details = () => {
    const classes = useDetailsClasses();
    const { t } = usePrefixTranslation('project', { project: 'scheduler.Details' });
    const dispatch = useTypedDispatch();
    const { plan, project, projectCategories } = useTypedSelector(state => ({
        plan: state.plan.changed,
        project: state.project.changed,
        projectCategories: state.project_category.items,
    }));
    const checkboxes = [
        { property: 'rope_access', label: t('checkbox-rope-access') },
        { property: 'internal_scaffold', label: t('checkbox-scaffolding') },
        { property: 'heavy_cleaning', label: t('checkbox-heavy-cleaning') },
    ] as const;

    React.useEffect(() => {
        dispatch(actions.project_category.get());
    }, []);

    if (!plan || !project) return <span>{'#plan or project not in store'}</span>;

    return (
        <div className={classes.root}>
            <div className={classes.inputs}>
                <Select
                    label={t('input-category')}
                    value={project.category}
                    options={Object.values(projectCategories).map(c => c.id)}
                    renderOption={option => projectCategories[option].name}
                    unselect={'None'}
                    onValue={value => dispatch(actions.project.edit({ category: value }))}
                />
                <Select
                    key={project.priority}
                    label={t('input-priority')}
                    defaultValue={project.priority}
                    options={probabilities}
                    renderOption={option => (
                        <Badge className={classes.badge} type={enumToStatusType[option]}>
                            {option}
                        </Badge>
                    )}
                    onValue={value => value && dispatch(actions.project.edit({ priority: value }))}
                />
            </div>
            <div className={classes.checkboxes}>
                {checkboxes.map(({ property, label }) => (
                    <CheckCard
                        key={property}
                        title={label}
                        editable
                        checked={project[property]}
                        onCheck={checked => dispatch(actions.project.edit({ [property]: checked }))}
                    />
                ))}
            </div>
            {['internal', 'external'].map(type => {
                const activity = plan?.inspection_activity?.find(a => a.type === type);
                if (!activity?.required) return undefined;
                const methods = Object.keys(activity.methods)
                    .filter(method => !method.startsWith('\n'))
                    .map(method => ({ method, checked: method in (project.methods[activity.type] ?? {}) }));
                return (
                    <div key={type} className={classes.activity}>
                        <span>{t(`activities-${activity.type}`)}</span>
                        <Divider />
                        <div className={classes.methods}>
                            {methods.map(({ method, checked }) => {
                                return (
                                    <CheckCard
                                        key={method}
                                        title={method}
                                        subtitle={t('performed-never')}
                                        editable={true}
                                        checked={checked}
                                        onCheck={checked => {
                                            const methods = JSON.parse(JSON.stringify(project.methods));
                                            if (!methods[activity.type]) methods[activity.type] = {};
                                            if (checked) methods[activity.type][method] = activity.methods[method];
                                            else delete methods[activity.type][method];
                                            const preparation_steps = { [activity.type]: activity.preparation_steps };
                                            const type =
                                                'internal' in methods && 'external' in methods
                                                    ? 'combined'
                                                    : 'internal' in methods
                                                    ? 'internal'
                                                    : 'external' in methods
                                                    ? 'external'
                                                    : 'combined';
                                            dispatch(actions.project.edit({ type, methods, preparation_steps }));
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
